import React, { useState, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import { makeStyles } from 'tss-react/mui'
import { Box, Unstable_Grid2 as Grid, Typography } from '@mui/material'

import HandleMarkup from '@src/components/HandleMarkup'
import LinkStyleButton from '@src/components/LinkStyleButton/LinkStyleButton'
import { Linq } from '@src/components/Linq'
import ResponsiveCatalogImage from '@src/components/ResponsiveCatalogImage/ResponsiveCatalogImage'
import {
  productDetailRoute,
  searchResultsRoute,
  substanceRoute,
  useRouter,
} from '@src/routes'
import {
  sendNewSrpProductClickEvent,
  sendProductInfoInteractionEvent,
} from '@src/utils/analytics'
import {
  determineCatalogType,
  formatCasNumberSearchTerm,
  getValidSearch,
  getValidSearchQuery,
  ProductSearchType,
} from '@src/utils/searchUtils'
import { useResponsive } from '@src/utils/useResponsiveSizes'

import { CompareProductCheckbox } from '../CompareProductCheckbox/CompareProductCheckbox'
import NewSrpKeyAttributes from '../NewSrpKeyAttributes/NewSrpKeyAttributes'
import NewSrpSdsLink from '../NewSrpSdsLink/NewSrpSdsLink'
import TogglePricingButton from '../TogglePricingButton/TogglePricingButton'
import ViewProductPricing from '../ViewProductOptions/ViewProductPricing'
import NewSrpProductPartnerInfo from '../NewSrpProductPartnerInfo'
import { AppCookies, useCookies } from '@src/utils/cookies'
import { sendPricingAvailabilityInteractionEventNewSrp } from '@src/utils/analytics/pricingAndAvailability'
import NewProductFeaturesBadge from '@src/components/NewProductFeaturesBadge'
import { useProductFeaturesBadge } from '@src/components/NewProductFeaturesBadge/useProductFeaturesBadge'

const useStyles = makeStyles()((theme) => {
  return {
    responsiveProductContainer: {
      margin: 0,
      padding: theme.spacing(6, 0),
    },
    productSpecsMainContainer: {
      maxWidth: 620,
    },
    thumbnailWrapper: {
      maxHeight: 88,
      maxWidth: 88,
      width: 88,
      height: 88,
      borderRadius: theme.spacing(1),
      border: `solid 1px ${theme.palette.grey[400]}`,
      backgroundColor: theme.palette.common.white,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: theme.typography.pxToRem(8),
      lineHeight: 0,
    },
    thumbnail: {
      maxWidth: 86,
      maxHeight: 86,
      width: 'auto',
      height: 'auto',
    },
    casNumberContainer: {
      marginTop: theme.spacing(2),
      display: 'inline-flex',
      alignItems: 'center',
    },
    casNumberLabel: {
      fontWeight: theme.typography.fontWeightMedium,
      marginRight: theme.spacing(4),
      fontSize: theme.typography.pxToRem(12),
    },
    casNumber: {
      curser: 'pointer',
      fontSize: theme.typography.pxToRem(12),
    },
    topRowContainer: {
      justifyContent: 'space-between',
    },
    productNumberStyle: {
      fontSize: theme.typography.pxToRem(16),
      marginBottom: theme.spacing(1),
    },
    productNumberMobileStyle: {
      marginTop: theme.spacing(6),
    },
    productNameStyle: {
      fontSize: theme.typography.pxToRem(18),
      fontWeight: theme.typography.fontWeightBold,
      color: theme.palette.common.black,
    },
    productNameMobileStyle: {
      marginTop: theme.spacing(1),
    },
    productNameSuffixStyle: {
      color: theme.palette.common.black,
      fontSize: theme.typography.pxToRem(16),
      fontWeight: theme.typography.fontWeightRegular,
      paddingTop: theme.spacing(4),
    },
    productSpecs: {
      paddingLeft: theme.spacing(6),
    },
    bottomRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    viewOptionsButton: {
      marginTop: theme.spacing(4),
    },
    compareProduct: {
      maxWidth: 290,
      padding: 0,
    },
    mobileRightCol: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      gap: theme.spacing(1),
    },
    mobileContainerRow: {
      maxWidth: 568,
      marginLeft: 'auto',
      marginRight: 'auto',
    },
    mobileButtonRow: {
      display: 'flex',
      justifyContent: 'center',
      marginTop: theme.spacing(6),
    },
    synonyms: {
      display: 'flex',
      marginBottom: theme.spacing(2),
      marginTop: theme.spacing(2),
    },
    synonymsLabel: {
      fontWeight: theme.typography.fontWeightMedium,
    },
    synonymsValues: {
      fontWeight: theme.typography.fontWeightRegular,
      paddingLeft: theme.spacing(4),
      '& sup': {
        lineHeight: 0,
      },
    },
    breakWord: {
      wordBreak: 'break-word',
    },
    productOptions: {
      width: '100%',
      [theme.breakpoints.up('lg')]: {
        marginLeft: '110px',
      },
    },
    substanceLinkWrapperMobile: {
      textAlign: 'center',
      wordBreak: 'break-word',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      display: '-webkit-box',
      '-webkit-line-clamp': '3',
      '-webkit-box-orient': 'vertical',
      maxWidth: '568px',
      [theme.breakpoints.up('sm')]: {
        display: '-webkit-box',
        '-webkit-line-clamp': '2',
        '-webkit-box-orient': 'vertical',
      },
    },
    desktopButtonRow: {
      display: 'flex',
      justifyContent: 'flex-end',
      verticalAlign: 'middle',
    },
    substanceLinkWrapperDesktop: {
      marginTop: theme.spacing(3),
      wordBreak: 'break-word',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      display: '-webkit-box',
      '-webkit-line-clamp': '2',
      '-webkit-box-orient': 'vertical',
    },
  }
})

const SdsSection = ({ product, sdsLinkEnabled }) => {
  if (!sdsLinkEnabled) return null

  return (
    <NewSrpSdsLink
      productNumber={product?.productNumber}
      brandKey={product?.brandKey}
      sdsLanguages={product?.sds}
    />
  )
}

const handleProductClickEvent = (product, substancePageRoute) => {
  sendProductInfoInteractionEvent({
    action: 'visit product substance page',
    detail: null,
    component: 'body',
    elementType: 'link',
    elementText: `view all products under ${product?.substance?.substanceName}`,
    linkUrl: substancePageRoute?.pathname,
    productId: product?.productNumber,
    productBrand: product?.brand,
  })
}
export const ResponsiveProduct = ({
  product,
  index,
  isBuyBoxSheetOpen,
  setIsBuyBoxSheetOpen,
  mobileSelectedMaterial,
  setMobileSelectedMaterial,
  handleCloseBuyBoxSheet,
}) => {
  const { classes } = useStyles()
  const { isMobile } = useResponsive()
  const router = useRouter()
  const [pnaExpanded, setPnaExpanded] = useState(false)
  const [, setCookie] = useCookies([AppCookies.SrpExperience])

  const IMG_SIZES = '88px'

  const {
    attributes,
    substance: { casNumber, substanceCount, substanceId, substanceName },
    keyAttributes,
    brandKey,
    legalName,
    legalNameSuffix,
    productKey,
    productNumber,
    productName,
    productNameSuffix,
    synonyms,
  } = product

  const { productFeaturesBadges } = useProductFeaturesBadge(attributes)

  const routerCatalogType = router.query.catalogType

  const validSearchQuery = getValidSearchQuery({
    focus: router.query.focus,
  })
  const catalogType = determineCatalogType(
    routerCatalogType ?? validSearchQuery.focus
  )
  const pdpRoute = useMemo(
    () =>
      productDetailRoute.index(
        brandKey,
        productKey.toLowerCase(),
        product.isBBE ? { context: 'bbe' } : {}
      ),
    [brandKey, productKey]
  )

  const substancePageRoute = useMemo(
    () => substanceRoute.index(substanceId, catalogType).as,
    [substanceId, catalogType]
  )

  const sdsLinkEnabled = useMemo(
    () => (product?.sds?.length ?? 0) > 0,
    [product?.sds?.length]
  )

  const handleToggleExpanded = () => {
    setPnaExpanded((prev) => !prev)
    if (isMobile && pnaExpanded) {
      handleCloseBuyBoxSheet()
    }

    sendPricingAvailabilityInteractionEventNewSrp({
      action: pnaExpanded ? 'close p&a' : 'view p&a',
      component: 'body',
      elementType: 'button',
      elementText: pnaExpanded ? 'hide pricing' : 'view pricing',
      product: product,
    })
  }

  // Handler for PDP link clicks
  const handlePdpLinkClick = (elementType) => {
    sendNewSrpProductClickEvent({
      product,
      elementType,
      substanceId,
      substanceName,
      productPosition: index + 1,
    })
  }

  const handleCasNumberClick = (product) => {
    const formattedCasNumber = formatCasNumberSearchTerm(casNumber)
    const casSearchPath = searchResultsRoute.searchResults(formattedCasNumber)
    sendProductInfoInteractionEvent({
      action: 'cas number click',
      detail: product?.substance?.casNumber,
      component: 'body',
      elementType: 'link',
      elementText: product?.substance?.casNumber,
      linkUrl: `${casSearchPath}${getValidSearch({
        term: formattedCasNumber.toLowerCase(),
        type: ProductSearchType.CasNumber,
      })}`,
      productId: product?.productNumber,
      productBrand: product?.brand,
    })
    setCookie(AppCookies.SrpExperience, 'old', { path: '/' })
    router.replace({
      pathname: `${casSearchPath}`,
      query: {
        term: casNumber,
        type: ProductSearchType.CasNumber,
      },
    })
  }

  const imageUrl = product.images?.[0]?.url
  const productImages = product?.images
  const name = legalName || productName
  const nameSuffix = legalNameSuffix || productNameSuffix
  const showSubstanceLink = substanceCount > 1 && substanceId && substanceName

  const renderDesktopLayout = () => (
    <Grid
      container
      className={classes.responsiveProductContainer}
      justifyContent="space-between"
      data-testid="new-srp-responsive-product"
    >
      <Grid md={8}>
        <Grid container className={classes.productSpecsMainContainer}>
          <Grid md={3} className={classes.thumbnailWrapper}>
            <Linq
              {...pdpRoute}
              onClick={() => handlePdpLinkClick('image')}
              data-testid={`srp-responsive-catalog-image-${imageUrl}`}
            >
              <ResponsiveCatalogImage
                className={classes.thumbnail}
                alt={product?.images?.[0]?.altText || productName}
                src={imageUrl}
                urls={imageUrl}
                sizes={IMG_SIZES}
              />
            </Linq>
          </Grid>

          <Grid xs>
            <Grid container direction="column" className={classes.productSpecs}>
              <Grid
                container
                direction="row"
                className={classes.topRowContainer}
              >
                <Grid className={classes.productNumberStyle}>
                  <Linq
                    {...pdpRoute}
                    role="link"
                    onClick={() => handlePdpLinkClick('link')}
                    data-testid={`srp-product-number-link-${productNumber}`}
                  >
                    {productNumber}
                  </Linq>
                </Grid>

                <SdsSection product={product} sdsLinkEnabled={sdsLinkEnabled} />
              </Grid>

              <Grid>
                <Linq
                  {...pdpRoute}
                  role="link"
                  className={classes.productNameStyle}
                  onClick={() => handlePdpLinkClick('link')}
                  data-testid={`srp-product-name-link-${name}`}
                >
                  <HandleMarkup value={name} />
                </Linq>
              </Grid>

              <Grid>
                <Linq
                  {...pdpRoute}
                  role="link"
                  className={classes.productNameSuffixStyle}
                  onClick={() => handlePdpLinkClick('link')}
                  data-testid={`srp-product-name-suffix-link-${nameSuffix}`}
                >
                  <HandleMarkup value={nameSuffix} />
                </Linq>
              </Grid>

              <Grid>
                <Box alignItems="center" display="flex" mt={2}>
                  {productFeaturesBadges.map((displayBadge) => (
                    <Box mr={4} key={displayBadge.id}>
                      <NewProductFeaturesBadge
                        color={displayBadge.color}
                        icon={displayBadge.icon}
                        iconAlt={displayBadge.iconAlt}
                        text={displayBadge.label}
                        textColor={displayBadge.textColor}
                        tooltipTitle={displayBadge.tooltipTitle}
                      />
                    </Box>
                  ))}
                </Box>
              </Grid>
              <Grid>
                <NewSrpProductPartnerInfo product={product} />
              </Grid>
              {synonyms?.length > 0 && (
                <Grid className={classes.synonyms}>
                  <Typography
                    component="span"
                    variant="body2"
                    className={classes.synonymsLabel}
                  >
                    <FormattedMessage
                      id="SYNONYM(S)"
                      defaultMessage="Synonym(s)"
                    />
                    {': '}
                  </Typography>
                  <Typography
                    component="span"
                    variant="body2"
                    className={classes.synonymsValues}
                  >
                    <HandleMarkup
                      className={classes.breakWord}
                      value={synonyms.join(', ')}
                    />
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Grid md={4} className={classes.compareProduct}>
        <CompareProductCheckbox
          productNumber={productNumber}
          productKey={productKey}
          brandKey={brandKey}
          productName={productName}
          catalogType={catalogType}
          images={productImages}
        />
        {casNumber && (
          <div
            className={classes.casNumberContainer}
            data-testid="srp-cas-number"
          >
            <Typography className={classes.casNumberLabel}>
              <FormattedMessage id="CAS_NUMBER" defaultMessage="CAS Number" />:{' '}
            </Typography>
            <LinkStyleButton
              className={classes.casNumber}
              onClick={() => {
                handleCasNumberClick(product)
              }}
            >
              {casNumber}
            </LinkStyleButton>
          </div>
        )}
        {keyAttributes && <NewSrpKeyAttributes keyAttributes={keyAttributes} />}
      </Grid>
      <Grid container direction="column" className={classes.productOptions}>
        <Grid container className={classes.bottomRow}>
          <Grid className={classes.viewOptionsButton}>
            <TogglePricingButton
              expanded={pnaExpanded}
              onToggle={handleToggleExpanded}
            />
          </Grid>
          {showSubstanceLink && (
            <Grid
              md={6}
              className={classes.desktopButtonRow}
              data-testid="new-srp-substance-link"
            >
              <Linq
                className={classes.substanceLinkWrapperDesktop}
                href={substancePageRoute}
                onClick={() =>
                  handleProductClickEvent(product, substancePageRoute)
                }
                onKeyUp={(event) => {
                  if (event.key === 'Enter') {
                    handleProductClickEvent(product, substancePageRoute)
                  }
                }}
                role="link"
              >
                <FormattedMessage
                  id="VIEW_ALL_PRODUCTS_UNDER"
                  defaultMessage="View All Products Under:"
                />{' '}
                <HandleMarkup value={substanceName} />
              </Linq>
            </Grid>
          )}
        </Grid>
        {pnaExpanded && (
          <Grid>
            <ViewProductPricing
              product={product}
              isBuyBoxSheetOpen={isBuyBoxSheetOpen}
              setIsBuyBoxSheetOpen={setIsBuyBoxSheetOpen}
              mobileSelectedMaterial={mobileSelectedMaterial}
              setMobileSelectedMaterial={setMobileSelectedMaterial}
              handleCloseBuyBoxSheet={handleCloseBuyBoxSheet}
            />
          </Grid>
        )}
      </Grid>
    </Grid>
  )

  const renderMobileLayout = () => (
    <Grid
      container
      className={classes.responsiveProductContainer}
      data-testid="new-srp-responsive-product-mobile"
    >
      <Grid xs={3}>
        <div className={classes.thumbnailWrapper}>
          <Linq {...pdpRoute} onClick={() => handlePdpLinkClick('image')}>
            <ResponsiveCatalogImage
              className={classes.thumbnail}
              alt={product?.images?.[0]?.altText || productName}
              src={imageUrl}
              urls={imageUrl}
              sizes={IMG_SIZES}
            />
          </Linq>
        </div>
      </Grid>

      <Grid xs={9}>
        <div className={classes.mobileRightCol}>
          {/* WILL UNCOMMENT DURING COMPARISON EPIC ON MOBILE 
          <CompareProductCheckbox
            productNumber={productNumber}
            productKey={productKey}
            brandKey={brandKey}
            productName={productName}
            catalogType={catalogType}
            images={productImages}
          /> */}

          <SdsSection product={product} sdsLinkEnabled={sdsLinkEnabled} />
        </div>
      </Grid>

      <Grid xs={12} className={classes.productNumberMobileStyle}>
        <Linq
          {...pdpRoute}
          role="link"
          className={classes.productNumberStyle}
          onClick={() => handlePdpLinkClick('link')}
        >
          {productNumber}
        </Linq>
      </Grid>

      <Grid xs={12} className={classes.productNameMobileStyle}>
        <Linq
          {...pdpRoute}
          role="link"
          className={classes.productNameStyle}
          onClick={() => handlePdpLinkClick('link')}
        >
          <HandleMarkup value={name} />
        </Linq>
      </Grid>

      <Grid xs={12} className={classes.productNameMobileStyle}>
        <Linq
          {...pdpRoute}
          role="link"
          className={classes.productNameSuffixStyle}
          onClick={() => handlePdpLinkClick('link')}
        >
          <HandleMarkup value={nameSuffix} />
        </Linq>
      </Grid>

      <Grid xs={12}>
        <Box alignItems="center" display="flex" mt={2}>
          {productFeaturesBadges.map((displayBadge) => (
            <Box mr={4} key={displayBadge.id}>
              <NewProductFeaturesBadge
                color={displayBadge.color}
                icon={displayBadge.icon}
                iconAlt={displayBadge.iconAlt}
                text={displayBadge.label}
                textColor={displayBadge.textColor}
                tooltipTitle={displayBadge.tooltipTitle}
              />
            </Box>
          ))}
        </Box>
      </Grid>

      {synonyms?.length > 0 && (
        <Grid className={classes.synonyms}>
          <Typography
            component="span"
            variant="caption"
            className={classes.synonymsLabel}
          >
            <FormattedMessage id="SYNONYM(S)" defaultMessage="Synonym(s)" />
            {': '}
          </Typography>
          <Typography
            component="span"
            variant="caption"
            className={classes.synonymsValues}
          >
            <HandleMarkup
              className={classes.breakWord}
              value={synonyms.join(', ')}
            />
          </Typography>
        </Grid>
      )}

      {casNumber && (
        <Grid className={classes.casNumberContainer} xs={12}>
          <Typography className={classes.casNumberLabel}>
            <FormattedMessage id="CAS_NUMBER" defaultMessage="CAS Number" />:{' '}
          </Typography>
          <LinkStyleButton
            className={classes.casNumber}
            onClick={() => {
              handleCasNumberClick(product)
            }}
          >
            {casNumber}
          </LinkStyleButton>
        </Grid>
      )}

      <Grid xs={12}>
        {keyAttributes && <NewSrpKeyAttributes keyAttributes={keyAttributes} />}
      </Grid>

      <Grid>
        <NewSrpProductPartnerInfo product={product} />
      </Grid>

      <Grid container xs={12} className={classes.mobileContainerRow}>
        <Grid
          xs={12}
          className={classes.mobileButtonRow}
          data-testid="new-srp-toggle-pricing"
        >
          <TogglePricingButton
            expanded={pnaExpanded}
            onToggle={handleToggleExpanded}
          />
        </Grid>
        {pnaExpanded && (
          <Grid xs={12}>
            <ViewProductPricing
              product={product}
              isBuyBoxSheetOpen={isBuyBoxSheetOpen}
              setIsBuyBoxSheetOpen={setIsBuyBoxSheetOpen}
              mobileSelectedMaterial={mobileSelectedMaterial}
              setMobileSelectedMaterial={setMobileSelectedMaterial}
              handleCloseBuyBoxSheet={handleCloseBuyBoxSheet}
            />
          </Grid>
        )}
        {substanceCount > 1 && substanceId && (
          <Grid xs={12} className={classes.mobileButtonRow}>
            <Linq
              className={classes.substanceLinkWrapperMobile}
              href={substancePageRoute}
              onClick={() =>
                handleProductClickEvent(product, substancePageRoute)
              }
              onKeyUp={(event) => {
                if (event.key === 'Enter') {
                  handleProductClickEvent(product, substancePageRoute)
                }
              }}
              role="link"
            >
              <FormattedMessage
                id="VIEW_ALL_PRODUCTS_UNDER"
                defaultMessage="View All Products Under:"
              />{' '}
              <HandleMarkup value={substanceName} />
            </Linq>
          </Grid>
        )}
      </Grid>
    </Grid>
  )

  return isMobile ? renderMobileLayout() : renderDesktopLayout()
}
